import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { getJobJourney, type JobJourney, type JourneyEntry } from '../../api/jobJourneyApi.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/** A job — or an order placed with no job (J1) — as the journey's own list
 *  returns it, named rather than identified. */
interface JobOption {
  readonly jobRequestId: string;
  readonly jobNumber?: string | null;
  readonly customerName?: string | null;
}

const STAGE_LABELS: Record<string, string> = {
  JOB: 'Job',
  VENDOR: 'Vendor',
  FIELD_FORCE: 'Field force',
  BRANDING: 'Branding',
  BO_FINAL: 'Final verification',
  BILLING: 'Billing',
  COMMERCIAL: 'Sales accounts',
  QUANTITY: 'Quantity',
  ORDER: 'Order placement',
  APPROVAL: 'Approval',
};

export function M08JobJourneyPage(): ReactNode {
  const { token } = useAuth();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const params = useParams();
  const [search, setSearch] = useSearchParams();
  const routeJobId = params.jobRequestId ?? search.get('jobRequestId') ?? '';

  const [jobId, setJobId] = useState(routeJobId);
  const [data, setData] = useState<JobJourney | null>(null);
  const [stage, setStage] = useState('ALL');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (id: string) => {
    if (!id.trim()) { setData(null); return; }
    setBusy(true); setError(null);
    try {
      setData(await getJobJourney(id.trim(), token));
    } catch (reason) {
      setError((reason as Error).message);
      setData(null);
    } finally {
      setBusy(false);
    }
  }, [token]);

  useEffect(() => { if (routeJobId) void load(routeJobId); }, [routeJobId, load]);

  // Stage list comes from the response, so a new stage needs no code change.
  const stages = useMemo(() => ['ALL', ...(data?.stages ?? [])], [data]);
  const rows = useMemo(
    () => (data?.entries ?? []).filter((e) => stage === 'ALL' || e.stage === stage),
    [data, stage],
  );

  return (
    <main aria-label="job-journey">
      <h1>Job Journey</h1>
      <p>Every step from job initiation to billing, with date, time, who acted and their remark.</p>

      {/* Asking for a uuid guaranteed the "job not found" the user reported:
          nobody can type one, and a typo is indistinguishable from a missing
          job. The jobs are on file, so they are offered by name. */}
      <form
        aria-label="journey-lookup"
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(jobId.trim() ? { jobRequestId: jobId.trim() } : {});
          void load(jobId);
        }}
      >
        <RecordPicker<JobOption>
          endpoint="/v1/job-journey"
          idField="jobRequestId"
          label="Job request"
          testId="jobRequestId"
          value={jobId}
          optionLabel={(j) =>
            [j.jobNumber, j.customerName].filter(Boolean).join(' \u2014 ') || j.jobRequestId
          }
          onPick={(id) => {
            setJobId(id);
            setError(null);
            setSearch(id ? { jobRequestId: id } : {});
            if (id) void load(id);
            else setData(null);
          }}
          emptyText="No job request on file"
        />
        <button disabled={busy || !jobId}>{busy ? 'Loading…' : 'Show journey'}</button>
      </form>

      {error ? <p role="alert">{error}</p> : null}

      {data ? (
        <>
          <p aria-label="journey-summary">
            {data.entryCount} step(s) recorded across {data.stages.length} stage(s).
          </p>

          <nav aria-label="journey-stages">
            {stages.map((code) => (
              <button
                key={code}
                type="button"
                aria-current={stage === code ? 'true' : undefined}
                onClick={() => setStage(code)}
              >
                {code === 'ALL' ? 'All' : (STAGE_LABELS[code] ?? code)}
              </button>
            ))}
          </nav>

          {rows.length === 0 ? (
            <p>No activity recorded yet for this job.</p>
          ) : (
            <table aria-label="journey-table">
              <thead>
                <tr>
                  <th scope="col">Date &amp; time</th>
                  <th scope="col">Stage</th>
                  <th scope="col">Activity</th>
                  <th scope="col">Done by</th>
                  <th scope="col">Remark</th>
                  <th scope="col">Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((entry: JourneyEntry, index) => (
                  <tr key={`${entry.source}-${at(entry.occurredAt)}-${index}`}>
                    <td>{at(entry.occurredAt)}</td>
                    <td>{STAGE_LABELS[entry.stage] ?? entry.stage}</td>
                    <td>{entry.action}</td>
                    <td>{entry.actorName ?? entry.actorUserId ?? '-'}</td>
                    <td>{entry.remark ?? '-'}</td>
                    <td>{entry.detail ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : null}
    </main>
  );
}
